// utils/sessionManager.js
// ============================================
// SESSION MANAGER - Handles multi-tab sessions
// ============================================

const SESSION_KEY = 'app_session_id';
const SESSION_DATA_PREFIX = 'session_';
const SESSION_HEARTBEAT_KEY = 'session_heartbeat_';
const HEARTBEAT_INTERVAL = 5000; // 5 seconds
const SESSION_TIMEOUT = 900000; // 15 minutes
// Written (with a fresh timestamp) whenever any tab logs out. localStorage
// writes fire a 'storage' event in every OTHER same-origin tab (never the
// tab that wrote it), so this is how one tab tells every other tab to end
// its own session too.
const LOGOUT_BROADCAST_KEY = 'app_logout_broadcast';
// Same idea, but for a successful login: token/role/profile fields are
// already written to shared localStorage on every login regardless of
// "remember me", so any other tab already HAS the data it needs after a
// reload — it just never knew to reload. This key is how a login tells
// every other open tab "the account changed, refresh yourself".
const LOGIN_BROADCAST_KEY = 'app_login_broadcast';

class SessionManager {
   constructor() {
    this.currentSessionId = null;
    this.heartbeatInterval = null;
    this.storageListener = null;
    this.beforeUnloadListener = null;
    this.isInitialized = false;

    this.cleanupStaleSessions();
    this.initSession();
  }

  initSession() {
    if (this.isInitialized) {
      return this.currentSessionId;
    }

    // sessionStorage is scoped to THIS tab only — it's never shared with
    // other tabs, even ones opened at the exact same time. Reuse this tab's
    // own session id if it already has one (e.g. surviving a same-tab
    // refresh); otherwise mint a new one.
    //
    // This used to call getActiveSession(), which scans localStorage for
    // ANY tab's active heartbeat and adopts that session id. That made a
    // brand-new tab silently "become" whatever session another tab had
    // open, and since session data is stored keyed by session id, both tabs
    // then read/write the same record — the source of the cross-tab
    // "wrong user's data" bug.
    const ownSessionId = sessionStorage.getItem(SESSION_KEY);
    this.currentSessionId = ownSessionId || this.generateSessionId();

    sessionStorage.setItem(SESSION_KEY, this.currentSessionId);
    this.startHeartbeat();
    this.setupStorageListener();
    
    this.isInitialized = true;

    return this.currentSessionId;
  }

  // Generate unique session ID
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  initData(userData) {
    this.initSession();
    // store session data in localStorage
    this.setSessionData(userData);

    // Tell every other open tab a login just happened so they can pick up
    // the (now shared, in localStorage) token/profile/role and reflect the
    // new account instead of sitting stale — or still showing the login
    // form — until someone manually reloads them.
    try {
      localStorage.setItem(LOGIN_BROADCAST_KEY, Date.now().toString());
    } catch (e) {
      // storage unavailable — other tabs simply won't auto-refresh
    }
  }

  // Get active session from any tab
  getActiveSession() {
    const now = Date.now();
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      if (key.startsWith(SESSION_HEARTBEAT_KEY)) {
        const sessionId = key.replace(SESSION_HEARTBEAT_KEY, '');
        const heartbeat = parseInt(localStorage.getItem(key), 10);

        // Check if heartbeat is recent (session is active)
        if (now - heartbeat < SESSION_TIMEOUT) {
          const sessionData = this.getSessionDataById(sessionId);
          if (sessionData) {
            return { sessionId, ...sessionData };
          }
        } else {
          // Clean up stale session
          this.cleanupSession(sessionId);
        }
      }
    }

    return null;
  }

  // Set session data
  setSessionData(userData) {
    const sessionData = {
      ...userData,
      timestamp: Date.now(),
    };
    localStorage.setItem(
      `${SESSION_DATA_PREFIX}${this.currentSessionId}`,
      JSON.stringify(sessionData)
    );
  }

  // Get session data by ID
  getSessionDataById(sessionId) {
    const data = localStorage.getItem(`${SESSION_DATA_PREFIX}${sessionId}`);
    return data ? JSON.parse(data) : null;
  }

  // Get current session data
  getSessionData() {
    if (!this.currentSessionId) {
      const storedSessionId = sessionStorage.getItem(SESSION_KEY);
      if (storedSessionId) {
        this.currentSessionId = storedSessionId;
      }
    }

    if (this.currentSessionId) {
      const sessionData = this.getSessionDataById(this.currentSessionId);
      if (sessionData) {
        return sessionData;
      }
    }

    // ✅ NEW: Fallback to localStorage user data if session is not found
    const fallbackUser = localStorage.getItem('user');
    if (fallbackUser) {
      try {
        const parsedUser = JSON.parse(fallbackUser);
        return {
          username: parsedUser.username || localStorage.getItem('employeeName'),
          email: localStorage.getItem('employeeEmail'),
          role: localStorage.getItem('role'),
          ...parsedUser
        };
      } catch (e) {
        console.error('Error parsing fallback user:', e);
      }
    }

    // ✅ NEW: Final fallback - construct from individual localStorage items
    const token = localStorage.getItem('token');
    if (token) {
      return {
        username: localStorage.getItem('employeeName') || 
                  localStorage.getItem('employeeEmail')?.split('@')[0] || 
                  'User',
        email: localStorage.getItem('employeeEmail'),
        role: localStorage.getItem('role'),
        token: token
      };
    }

    return null;
  }

  // Start heartbeat to keep session alive
  startHeartbeat() {
    this.updateHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      this.updateHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }

  // Update heartbeat timestamp
  updateHeartbeat() {
    if (this.currentSessionId) {
      localStorage.setItem(
        `${SESSION_HEARTBEAT_KEY}${this.currentSessionId}`,
        Date.now().toString()
      );
    }
  }

  // Setup storage listener for cross-tab communication
  setupStorageListener() {
    this.storageListener = (e) => {
      // Any tab (including this one's own previous instance) triggered a
      // full logout — end this tab's session too, regardless of whether it
      // shares a session id with the tab that logged out.
      if (e.key === LOGOUT_BROADCAST_KEY && e.newValue) {
        this.handleSessionClear();
        return;
      }

      // Another tab just logged in (possibly as a different account).
      // token/role/profile fields are already shared via localStorage, so
      // this tab already has everything it needs — it just never knew to
      // look. Hard-navigate to "/" (same style as the logout broadcast
      // hard-navigating to "/login") so the app boots fresh: App.js's
      // DefaultRoute reads sessionManager.isAuthenticated()/getUserRole()
      // straight from (now updated) storage and sends this tab to the
      // right dashboard — no matter what page it was previously showing.
      if (e.key === LOGIN_BROADCAST_KEY && e.newValue) {
        window.location.href = '/';
        return;
      }

      // This tab's own session record was cleared directly.
      if (e.key === `${SESSION_DATA_PREFIX}${this.currentSessionId}` && !e.newValue) {
        this.handleSessionClear();
      }
    };

    window.addEventListener('storage', this.storageListener);

    // Also handle tab close/refresh. Keep a reference so cleanup() can
    // remove it — otherwise every logout+re-login within the same tab (no
    // full page reload) piles on another duplicate listener.
    this.beforeUnloadListener = () => {
      this.updateHeartbeat(); // One last heartbeat
    };
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  // Handle session cleared by this tab or broadcast from another tab
  handleSessionClear() {
    this.cleanup();
    // Shared localStorage auth keys are already cleared by whichever tab
    // initiated the logout; this tab's own (non-shared) sessionStorage copy
    // is not, so clear it here.
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    window.location.href = '/login';
  }

  // Check if user is authenticated
  isAuthenticated() {
    // Only ever validate THIS tab's own session id — never adopt another
    // tab's session id here (see initSession() for why that caused two tabs
    // to end up sharing, and clobbering, the same session record).
    if (this.currentSessionId) {
      const heartbeat = parseInt(
        localStorage.getItem(`${SESSION_HEARTBEAT_KEY}${this.currentSessionId}`),
        10
      );
      const hasFreshHeartbeat = !isNaN(heartbeat) && (Date.now() - heartbeat) < SESSION_TIMEOUT;
      const sessionData = this.getSessionDataById(this.currentSessionId);

      if (hasFreshHeartbeat && sessionData) {
        return true;
      }
    }

    // ✅ ENHANCED: Check localStorage for valid token or user data (fallback)
    const token = localStorage.getItem('token');
    const localUser = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('user');
    
    return !!(token || localUser || sessionUser);
  }

  // Get user role
  getUserRole() {
    const sessionData = this.getSessionData();
    if (sessionData && sessionData.role) {
      return sessionData.role;
    }

    // Fallback to localStorage
    return localStorage.getItem('role');
  }

  // ✅ NEW: Get user data (for compatibility with AuthContext)
  getUser() {
    const sessionData = this.getSessionData();
    if (sessionData) {
      return {
        username: sessionData.username || sessionData.employeeName,
        email: sessionData.email || sessionData.employeeEmail,
        role: sessionData.role,
        ...sessionData
      };
    }

    // Fallback to localStorage 'user' key
    const localUser = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('user');
    
    if (localUser) {
      try {
        return JSON.parse(localUser);
      } catch (e) {
        console.error('Error parsing local user:', e);
      }
    }
    
    if (sessionUser) {
      try {
        return JSON.parse(sessionUser);
      } catch (e) {
        console.error('Error parsing session user:', e);
      }
    }

    return null;
  }

  // Cleanup session for current tab
  cleanup() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }

    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }

    // Remove this tab's session record entirely (data + heartbeat) — not
    // just the heartbeat. Leaving the data behind meant a "cleared" session
    // could still be read back by getSessionData()/getActiveSession().
    if (this.currentSessionId) {
      localStorage.removeItem(`${SESSION_DATA_PREFIX}${this.currentSessionId}`);
      localStorage.removeItem(`${SESSION_HEARTBEAT_KEY}${this.currentSessionId}`);
    }

    // Clear session storage for this tab
    sessionStorage.removeItem(SESSION_KEY);

    // Reset so a subsequent login in this same tab (no full page reload)
    // starts a genuinely fresh session — generateSessionId() + a new
    // heartbeat/listener — instead of initSession() seeing isInitialized
    // still true and silently reusing this now-dead session with no
    // heartbeat running.
    this.currentSessionId = null;
    this.isInitialized = false;
  }

  // Cleanup specific session
  cleanupSession(sessionId) {
    localStorage.removeItem(`${SESSION_DATA_PREFIX}${sessionId}`);
    localStorage.removeItem(`${SESSION_HEARTBEAT_KEY}${sessionId}`);
  }

  // Logout - clear this tab's session AND tell every other open tab to log out too
  logout() {
    // Clear standard (shared) auth data
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');

    // Broadcast to every other tab. Each tab's own storage listener
    // (setupStorageListener) picks this up and runs handleSessionClear(),
    // which cleans up ITS OWN session and hard-redirects to /login — this
    // is what makes logout end the session everywhere, not just here.
    try {
      localStorage.setItem(LOGOUT_BROADCAST_KEY, Date.now().toString());
    } catch (e) {
      // storage unavailable (e.g. private mode quota) — this tab still logs out below
    }

    // Cleanup this tab's own session (clears interval + listeners + its
    // own session data/heartbeat record).
    this.cleanup();
  }

  // Clean up all stale sessions
  cleanupStaleSessions() {
    const now = Date.now();
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith(SESSION_HEARTBEAT_KEY)) {
        const sessionId = key.replace(SESSION_HEARTBEAT_KEY, '');
        const heartbeat = parseInt(localStorage.getItem(key), 10);

        if (now - heartbeat >= SESSION_TIMEOUT) {
          this.cleanupSession(sessionId);
        }
      }
    });
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();